import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';

import { setAttr } from 'redom';

import application from 'application';
import serverInterface from 'server-interface';

import { isStrictValidEmail } from 'utilities';
import { differenceByProperty } from 'utilities/collection';
import { displayElementAroundAnchor } from 'utilities/element-position';

import NavigableList from 'generics/components/navigable-list';

import useOnClickOutside from 'generics/hooks/on-click-outside';
import useOnEscapeKey from 'generics/hooks/on-escape-key';

import Contact from './contact';
import MeetingAttendee from './meeting-attendee';

import {
  applyStartupMemberEmailAddresses
} from '../utilities';

import { ReactComponent as CloseIcon } from 'assets/images/close-button-icon.svg';

import './index.scss';

const MINIMUM_CHARACTERS_COUNT = 2;

function ContactsInput(props) {

  const { contacts, type, onChange } = props;

  const contactInputControlReference = useRef(null);
  const contactInputWrapperReference = useRef(null);
  const [rootState, setRootState] = useState({
    searchTerm: '',
    searching: false,
    contacts: [],
    selectedItemIndex: 0,
    displayNoResultsFound: false,
    displayedContacts: [...contacts],
    limit: props.limit || 1
  });
  const [listReference, setListReference] = useState(null);
  const [extrasWrapperReference, setExtrasWrapperReference] = useState(null);

  useOnClickOutside(contactInputWrapperReference, hideNavigableList);
  useOnClickOutside(contactInputWrapperReference, hideNoResultsFoundBanner);
  useOnEscapeKey(hideNoResultsFoundBanner);

  useEffect(function onInputElementChange() {

    tryAndFocusOnInput();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactInputControlReference.current]);

  useEffect(function onLoad() {

    tryAndFocusOnInput();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(function onNavigableListVisibilityChange() {

    if (listReference === null) {
      return;
    }

    tryPositionNavigableList(listReference);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listReference]);

  useLayoutEffect(function onExtrasWrapperVisibilityChange() {

    if (extrasWrapperReference === null) {
      return;
    }

    const element = extrasWrapperReference;
    const anchorElement = contactInputControlReference.current;
    const viewportElement = void 0;
    const verticalOffset = 0;
    const horizontalOffset = 0;
    const startSide = 'bottom';
    const placeOnCenter = true;
    const positionableSides = ['bottom'];

    setAttr(element, {
      style: {
        width: `${contactInputControlReference.current.clientWidth}px`
      }
    });

    displayElementAroundAnchor(element, anchorElement, viewportElement, verticalOffset, horizontalOffset, startSide, placeOnCenter, positionableSides);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extrasWrapperReference]);

  useEffect(function onLimitChange() {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        limit: props.limit
      };

    });

  }, [props.limit]);

  useEffect(function onLimitChange() {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        contacts: [],
        displayedContacts: [...props.contacts]
      };

    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.contacts]);

  function canRemove(contact) {

    if (typeof props.canRemove === 'function') {
      return props.canRemove(contact);
    }

    return true;

  }

  function tryPositionNavigableList(element) {

    const anchor = contactInputControlReference.current;

    if (anchor === null) {
      return;
    }

    const viewport = document.body;
    const horizontalOffset = 5;
    const verticalOffset = 5;
    const startSide = void 0;
    const placeOnCenter = false;
    const positionableSides = ['top', 'bottom'];

    setAttr(element, {
      style: {
        width: `${element.parentNode.clientWidth}px`
      }
    });

    displayElementAroundAnchor(element, anchor, viewport, verticalOffset, horizontalOffset, startSide, placeOnCenter, positionableSides);

  }

  function handleRemoveContactClick(index) {

    const _contacts = [...rootState.displayedContacts];
    _contacts.splice(index, 1);
    setRootState({
      ...rootState,
      displayedContacts: _contacts
    });
    onChange(_contacts);

    /* beautify ignore:start */
    contactInputWrapperReference.current?.focus();
    /* beautify ignore:end */

  }

  function hideNoResultsFoundBanner() {

    setRootState({
      ...rootState,
      displayNoResultsFound: false
    });

  }

  function tryAndFocusOnInput() {

    const contactInputControlElement = contactInputControlReference.current;

    if (contactInputControlElement === null) {
      return;
    }

    if (props.autoFocus === false) {
      return;
    }

    contactInputControlElement.focus();

  }

  async function defaultSearch(searchTerm) {

    const contacts = await serverInterface.startup.users.getStartupUserContactsByQueryOptions({
      filterOptions: {
        type,
        searchTerm
      }
    });

    return contacts;

  }

  async function handleContactSearch(searchTerm) {

    let searchFunction = defaultSearch;

    if (typeof props.customSearchFunction === 'function') {
      searchFunction = props.customSearchFunction;
    }

    const contacts = await searchFunction(searchTerm);

    return contacts;

  }

  async function handleSearchTermChange(searchTerm) {

    let searching = searchTerm.length > MINIMUM_CHARACTERS_COUNT;

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        searching,
        selectedItemIndex: 0,
        contacts: [],
        searchTerm,
        displayNoResultsFound: false
      };

    });

    if (searching === false) {
      return;
    }

    if (typeof props.onSearch === 'function') {
      props.onSearch(searchTerm, isStrictValidEmail(searchTerm));
    }

    const contacts = await handleContactSearch(searchTerm);
    const memberMap = application.startupModule.members.getMemberMap();

    applyStartupMemberEmailAddresses(memberMap, contacts);

    setRootState(function updateRootState(rootState) {

      if (rootState.searchTerm !== searchTerm) {
        return rootState;
      }

      const remaining = differenceByProperty(contacts, rootState.displayedContacts, 'value');

      return {
        ...rootState,
        searching: false,
        contacts: remaining,
        displayNoResultsFound: remaining.length === 0 && isStrictValidEmail(searchTerm)
      };

    });

  }

  function handleContactInputChange(event) {

    let searchTerm = event.target.value;

    handleSearchTermChange(searchTerm);

  }

  function handleSelectNavigableItem(item) {

    const displayedContacts = [...rootState.displayedContacts, item];

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        searchTerm: '',
        contacts: [],
        selectedItemIndex: 0,
        searching: false,
        displayNoResultsFound: false,
        displayedContacts
      };

    });

    onChange(displayedContacts);

    tryAndFocusOnInput();

  }

  function handleUpArrowEvent() {

    let selectedItemIndex = rootState.selectedItemIndex;

    if (selectedItemIndex === 0) {
      selectedItemIndex = rootState.contacts.length;
    }

    selectedItemIndex--;

    setRootState({ ...rootState, selectedItemIndex });

  }

  function handleDownArrowEvent() {

    let selectedItemIndex = rootState.selectedItemIndex;

    if (selectedItemIndex === rootState.contacts.length - 1) {
      selectedItemIndex = -1;
    }

    selectedItemIndex++;

    setRootState({ ...rootState, selectedItemIndex });

  }

  async function selectSearchTerm(value) {

    try {

      const contact = await props.addContact(value);

      if (contact === null) {
        return;
      }

      handleSelectNavigableItem(contact);

    } catch (exception) {

      // eslint-disable-next-line no-console
      console.error(exception);

    }

  }

  function handleContactInputKeysEvent(event) {

    const code = event.code;

    if (rootState.contacts.length === 0) {

      if (code !== 'Enter') {
        return;
      }

      const value = event.target.value;

      selectSearchTerm(value);

      return;
    }

    if (code === 'Enter') {
      handleSelectNavigableItem(rootState.contacts[rootState.selectedItemIndex]);
    } else if (code === 'ArrowUp') {
      handleUpArrowEvent();
    } else if (code === 'ArrowDown') {
      handleDownArrowEvent();
    } else if (code === 'Escape') {
      hideNavigableList();
    }

  }

  function hideNavigableList() {

    setRootState({
      ...rootState,
      contacts: [],
      selectedItemIndex: 0,
      searching: false
    });

  }

  function renderSpinner() {

    if (rootState.searching === false) {
      return;
    }

    const wrapperAttributes = {
      className: 'contact-input-extras-wrapper spinner-container',
      ref(el) { setExtrasWrapperReference(el); }
    };

    return <div {...wrapperAttributes}>
      <ht-spinner size="small" />
    </div>;
  }

  function renderNoResultFoundContainer() {

    if (rootState.displayNoResultsFound === false) {
      return;
    }

    const contactInputExtrasWrapperAttributes = {
      className: 'navigable-list-main',
      ref(el) { setExtrasWrapperReference(el); }
    };

    if (props.allowManualContact === true) {
      contactInputExtrasWrapperAttributes.className += ' clickable';
      contactInputExtrasWrapperAttributes.onClick = function clickHandler() {
        selectSearchTerm(rootState.searchTerm);
      };
    }

    return <div {...contactInputExtrasWrapperAttributes}>
      <ul className="items-container helptap-list">
        <li class="item-container">
          <div class="contact-item navigable-list-item">
            <img src="/assets/images/default-profile-picture.png" class="contact-profile-picture" />
            <span class="contact-profile-username ellipsis">{rootState.searchTerm} <span class="application-sub-text">({rootState.searchTerm})</span></span>
          </div>
        </li>
      </ul>
    </div>;
  }

  function renderNavigableListItem(item, index, selectedItemIndex) {

    const contactItemNavigableListItemAttributes = {
      className: 'contact-item navigable-list-item'
    };

    if (index === selectedItemIndex) {
      contactItemNavigableListItemAttributes.className += ' selected';
    }

    const contactProfileImageAttributes = {
      src: item.profilePicture.url,
      className: 'contact-profile-picture'
    };

    const contactProfileImageDescription = `${item.name} Profile Image`;

    return <div {...contactItemNavigableListItemAttributes}>
      <img {...contactProfileImageAttributes} alt={contactProfileImageDescription} />
      <span className="contact-profile-username ellipsis">{item.name || item.value}{' '}<span className="application-sub-text">({item.value})</span></span>
    </div>;
  }

  function renderNavigableListControl() {

    if (rootState.contacts.length === 0) {
      return;
    }

    const navigableListProperties = {
      ref(el) { setListReference(el); },
      items: rootState.contacts,
      selectedItemIndex: rootState.selectedItemIndex,
      itemRenderer: renderNavigableListItem,
      onSelect(item) {
        handleSelectNavigableItem(item);
      },
      onClose: hideNavigableList
    };

    return <NavigableList {...navigableListProperties} />;
  }

  function renderContactsInput(contactsLength) {

    if (rootState.displayedContacts.length >= rootState.limit || props.disabled === true) {
      return;
    }

    const contactAddressInputAttributes = {
      className: 'contact-address-input',
      type: 'text',
      placeholder: props.placeholder,
      ref: contactInputControlReference,
      value: rootState.searchTerm,
      onInput(event) {
        handleContactInputChange(event);
      },
      onKeyDown(event) {
        handleContactInputKeysEvent(event);
      },
      autoFocus: true
    };

    const contactInputWrapperAttributes = {
      className: 'contact-input-wrapper',
      ref: contactInputWrapperReference
    };

    return <div key={contactsLength} {...contactInputWrapperAttributes}>
      <input {...contactAddressInputAttributes} />

      {renderSpinner()}

      {renderNoResultFoundContainer()}

      {renderNavigableListControl()}
    </div>;
  }

  function renderRemoveContactItemControl(contact, index) {

    if (props.disabled === true) {
      return;
    }

    if (canRemove(contact) === false) {
      return;
    }

    const closeButtonAttributes = {
      className: 'application-button round-button contact-profile-remove-button',
      onClick() {
        handleRemoveContactClick(index);
      }
    };

    return <button {...closeButtonAttributes}>
      <CloseIcon />
    </button>;
  }

  function renderContact(contact, index) {

    const properties = {
      contact,
      disabled: props.disabled,
      canRemove,
      onRemove() {
        handleRemoveContactClick(index);
      }
    };

    /* eslint-disable indent */
    switch (props.contactRenderer) {

      case 'meeting-attendee':
        return <MeetingAttendee key={index} {...properties} />;
      default:
        return <Contact key={index} {...properties} />;

    }
    /* eslint-disable indent */

  }

  function renderContacts() {

    let elements = [];
    const displayedContacts = rootState.displayedContacts;
    const contactsLength = displayedContacts.length;
    const inputElement = renderContactsInput(contactsLength);

    for (let index = 0; index < contactsLength; index++) {

      const contact = displayedContacts[index];
      const element = renderContact(contact, index);

      elements.push(element);

    }

    elements = [...elements, inputElement];

    return <div className="contacts-list">{elements}</div>;

  }

  function renderContactsInputBody() {

    return <div className="contact-address-input-body">
      {renderContacts()}
    </div>;

  }

  function renderMain() {

    const mainAttributes = {
      className: 'contact-address-input-main',
      onClick() {

        if (contactInputControlReference.current === null) {
          return;
        }

        contactInputControlReference.current.focus();
      }
    };

    return <div {...mainAttributes}>
      {renderContactsInputBody()}
    </div>;

  }

  return renderMain();

}

export default ContactsInput;

export function render(properties) {
  return <ContactsInput {...properties} />;
}